/**
 * IELTS Speaking with the live examiner (D-105), built on the TCF conversation pieces (level
 * meter, examiner avatar, bubbles): microphone test, then Parts 1, 2 and 3. The examiner speaks
 * British English (en-GB). Part 2: the cue card appears with the minute of preparation (server
 * clock), then 2 minutes of speaking and a rounding-off question. Part 3: a discussion. Every
 * turn is sent to the server (transcribed, answered); the whole part is also recorded and sent.
 */

import { academyAttemptsNextCreate, academyAttemptsRecordingCreate, academyAttemptsSpeakingCreate } from "@agencyos/api-client";
import { type IeltsState, type IeltsTask, type TurnListening, listenTurn, startTurn } from "@agencyos/core";
import { Button } from "@agencyos/ui-web";
import { MessageSquareReply, Mic, MicOff, NotebookPen, Square } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { closeStream, extensionFor, openMicrophone, recorderMimeType, recorderOptions, sendRecording, stopSpeaking, useMicLevel, useMicStayedSilent} from "../audio";
import { usePhaseCountdown } from "../shared";
import { Examiner, LevelMeter } from "../SpeakingSection";
import { clock } from "../text";
import { Ring } from "../ui";
import { speakEnglish } from "./speech";
import { isNoOralCredits } from "@agencyos/core";
import { OralCreditsNotice, reportNoOralCredits } from "../TrainerReview";

const VOICE_LEVEL = 0.06;
/** Part 2: time left after the long turn for the rounding-off question. */
const FOLLOW_UP_SECONDS = 60;

type Phase = "mic" | "ready" | "opening" | "prep" | "record" | "upload" | "uploadError" | "between" | "review";
type LiveStatus = "idle" | "listening" | "sending" | "speaking";
interface Line {
  role: "learner" | "examiner";
  text: string;
}

/** Seconds from now to an ISO date (the server keeps the preparation time). */
function secondsUntil(iso: string | null, fallback: number): number {
  if (!iso) return fallback;
  const at = Date.parse(iso);
  return Number.isNaN(at) ? fallback : Math.max(0, Math.round((at - Date.now()) / 1000));
}

/** The live turns of a part: each learner turn is sent, the examiner's reply is read aloud. */
function useIeltsExaminer({
  attemptId,
  task,
  stream,
  level,
  active,
  onEnd,
}: {
  attemptId: string;
  task: IeltsTask | undefined;
  stream: MediaStream | null;
  level: number;
  active: boolean;
  onEnd: () => void;
}) {
  const [status, setStatus] = useState<LiveStatus>("idle");
  const [lines, setLines] = useState<Line[]>([]);
  const [offline, setOffline] = useState(false);
  const [turns, setTurns] = useState(0);
  const segment = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const listening = useRef<TurnListening>(startTurn(0));
  const segmentStart = useRef(0);
  const partStart = useRef(0);
  const activeRef = useRef(active);
  const statusRef = useRef<LiveStatus>("idle");
  const endRef = useRef(onEnd);
  useEffect(() => {
    endRef.current = onEnd;
  }, [onEnd]);
  const change = (next: LiveStatus) => {
    statusRef.current = next;
    setStatus(next);
  };

  const startSegment = useCallback(() => {
    if (!stream || !activeRef.current || typeof MediaRecorder === "undefined") return;
    const mimeType = recorderMimeType();
    const media = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    chunks.current = [];
    media.ondataavailable = (event) => {
      if (event.data.size) chunks.current.push(event.data);
    };
    segment.current = media;
    segmentStart.current = Date.now();
    listening.current = startTurn(segmentStart.current);
    media.start();
    change("listening");
  }, [stream]);

  const answer = useCallback(
    async (blob: Blob) => {
      if (!task) return;
      change("sending");
      const type = blob.type || "audio/webm";
      const file = new File([blob], `speaking-turn.${extensionFor(type)}`, { type });
      const offset = Math.max((segmentStart.current - partStart.current) / 1000, 0);
      let reply = "";
      let end = false;
      try {
        const response = await academyAttemptsSpeakingCreate(attemptId, { task: task.id, audio: file, offset });
        const data = response.data;
        reply = data.reply;
        end = data.end;
        setOffline(false);
        setLines((list) => [
          ...list,
          ...(data.transcript ? [{ role: "learner" as const, text: data.transcript }] : []),
          ...(reply ? [{ role: "examiner" as const, text: reply }] : []),
        ]);
      } catch (error) {
        if (isNoOralCredits(error)) reportNoOralCredits(); // D-124: polite notice, no credit left
        setOffline(true);
      }
      setTurns((n) => n + 1);
      if (!activeRef.current) return;
      if (reply) {
        change("speaking");
        await speakEnglish(reply);
      }
      if (!activeRef.current) return;
      if (end) {
        change("idle");
        endRef.current();
      } else startSegment();
    },
    [attemptId, task, startSegment],
  );

  const finishTurn = useCallback(() => {
    const media = segment.current;
    if (!media || media.state !== "recording" || statusRef.current !== "listening") return;
    media.onstop = () => void answer(new Blob(chunks.current, { type: media.mimeType || "audio/webm" }));
    media.stop();
  }, [answer]);

  useEffect(() => {
    activeRef.current = active;
    if (active) {
      partStart.current = Date.now();
      setTurns(0);
      startSegment();
      return;
    }
    const media = segment.current;
    if (media && media.state === "recording") {
      media.onstop = null;
      media.stop();
    }
    segment.current = null;
    change("idle");
  }, [active, startSegment]);

  useEffect(
    () => () => {
      activeRef.current = false;
      const media = segment.current;
      if (media && media.state === "recording") {
        media.onstop = null;
        media.stop();
      }
    },
    [],
  );

  // Part 2: the long turn is never cut by a pause (the learner or the clock ends it).
  const longTurn = task?.number === 2 && turns === 0;
  useEffect(() => {
    if (!active || status !== "listening" || longTurn) return;
    const next = listenTurn(listening.current, level > VOICE_LEVEL, Date.now());
    listening.current = next;
    if (next.end) finishTurn();
  }, [level, active, status, finishTurn, longTurn]);

  return { status, lines, setLines, offline, finishTurn, longTurn };
}

export function IeltsSpeakingSection({ attemptId, state, onChanged }: { attemptId: string; state: IeltsState; onChanged: () => Promise<void> }) {
  const { t } = useTranslation();
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [micError, setMicError] = useState(false);
  const [phase, setPhase] = useState<Phase>("mic");
  const [done, setDone] = useState<string[]>(() => state.tasks.filter((task) => task.hasAudio).map((task) => task.id));
  const [card, setCard] = useState<{ topic: string; bullets: string[] } | null>(null);
  const [prepSeconds, setPrepSeconds] = useState(0);
  const [opening, setOpening] = useState("");
  const [finishing, setFinishing] = useState(false);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const pendingBlob = useRef<Blob | null>(null);
  const level = useMicLevel(stream);
  const micSilent = useMicStayedSilent(level, phase === "record");

  const task = state.tasks.find((item) => !done.includes(item.id));
  const speakSeconds = task ? task.seconds + (task.number === 2 ? FOLLOW_UP_SECONDS : 0) : 0;

  useEffect(
    () => () => {
      stopSpeaking();
      if (recorder.current?.state === "recording") recorder.current.stop();
    },
    [],
  );
  useEffect(() => () => closeStream(stream), [stream]);
  // The last task is over: the microphone is released at once (D-114), not when leaving.
  useEffect(() => {
    if (phase === "review") setStream(null);
  }, [phase]);

  const testMicrophone = async () => {
    setMicError(false);
    try {
      setStream(await openMicrophone());
      setPhase("ready");
    } catch {
      setMicError(true);
    }
  };

  const upload = useCallback(
    async (blob: Blob, current: IeltsTask) => {
      setPhase("upload");
      const type = blob.type || "audio/webm";
      const file = new File([blob], `speaking-part-${current.number}.${extensionFor(type)}`, { type });
      try {
        await sendRecording(() => academyAttemptsRecordingCreate(attemptId, { task: current.id, audio: file }));
        pendingBlob.current = null;
        setDone((list) => [...list, current.id]);
        setPhase("between");
      } catch {
        pendingBlob.current = blob;
        setPhase("uploadError");
      }
    },
    [attemptId],
  );

  const startRecording = useCallback(() => {
    if (!stream || !task) return;
    const mimeType = recorderMimeType();
    const media = new MediaRecorder(stream, recorderOptions(mimeType));
    chunks.current = [];
    media.ondataavailable = (event) => {
      if (event.data.size) chunks.current.push(event.data);
    };
    const current = task;
    media.onstop = () => {
      const blob = new Blob(chunks.current, { type: media.mimeType || mimeType || "audio/webm" });
      void speakEnglish(t("ielts.examinerEn.thanks"));
      void upload(blob, current);
    };
    recorder.current = media;
    media.start(1000);
    setPhase("record");
  }, [stream, task, t, upload]);

  const stopRecording = useCallback(() => {
    if (recorder.current?.state === "recording") recorder.current.stop();
  }, []);

  const live = useIeltsExaminer({ attemptId, task, stream, level, active: phase === "record", onEnd: stopRecording });
  const { setLines, finishTurn, longTurn } = live;

  /** The examiner opens the part (question, or cue card and the minute of preparation). */
  const beginPart = useCallback(async () => {
    if (!task) return;
    setPhase("opening");
    setCard(null);
    let reply = "";
    let prepUntil: string | null = null;
    try {
      const response = await academyAttemptsSpeakingCreate(attemptId, { task: task.id });
      reply = response.data.reply;
      prepUntil = response.data.prep_until;
      const cue = response.data.cue_card;
      if (cue && typeof cue === "object" && "topic" in cue) {
        const value = cue as { topic?: unknown; bullets?: unknown };
        setCard({
          topic: typeof value.topic === "string" ? value.topic : task.prompt,
          bullets: Array.isArray(value.bullets) ? value.bullets.map(String) : task.bullets,
        });
      } else if (task.number === 2) setCard({ topic: task.prompt, bullets: task.bullets });
    } catch (error) {
      if (isNoOralCredits(error)) reportNoOralCredits(); // D-124: polite notice, no credit left
      reply = task.number === 2 ? task.prompt : t("ielts.examinerEn.fallback");
      if (task.number === 2) setCard({ topic: task.prompt, bullets: task.bullets });
    }
    setOpening(reply);
    setLines(reply ? [{ role: "examiner", text: reply }] : []);
    await speakEnglish(reply);
    if (task.number === 2 || task.prepSeconds > 0) {
      setPrepSeconds(secondsUntil(prepUntil, task.prepSeconds || 60));
      setPhase("prep");
    } else startRecording();
  }, [attemptId, task, t, startRecording, setLines]);

  useEffect(() => {
    if (phase !== "between") return;
    const timer = window.setTimeout(() => {
      if (task) void beginPart();
      else setPhase("review");
    }, 2200);
    return () => window.clearTimeout(timer);
  }, [phase, task, beginPart]);

  const prepLeft = usePhaseCountdown(prepSeconds, phase === "prep", () => {
    void speakEnglish(t("ielts.examinerEn.startSpeaking")).then(startRecording);
  });
  const speakLeft = usePhaseCountdown(speakSeconds, phase === "record", stopRecording);
  // Part 2: at the end of the 2 minutes the long turn is handed to the examiner.
  const longLeft = task?.number === 2 ? speakLeft - FOLLOW_UP_SECONDS : speakLeft;
  useEffect(() => {
    if (phase === "record" && longTurn && longLeft <= 0) finishTurn();
  }, [phase, longTurn, longLeft, finishTurn]);

  const finish = async () => {
    setFinishing(true);
    try {
      await academyAttemptsNextCreate(attemptId);
    } finally {
      await onChanged();
      setFinishing(false);
    }
  };

  const noRecorder = typeof MediaRecorder === "undefined";
  const shownLeft = phase === "prep" ? prepLeft : live.longTurn ? Math.max(0, longLeft) : speakLeft;
  const shownTotal = phase === "prep" ? prepSeconds || 60 : live.longTurn ? (task?.seconds ?? 120) : speakSeconds;

  return (
    <section className="ac-speaking" aria-label={t("ielts.skill.eo")}>
      <div className="ac-question__sheet ac-speaking__sheet">
        <OralCreditsNotice />
        {phase === "mic" && (
          <div className="ac-stage">
            <span className="ac-stage__mic" aria-hidden="true">
              <Mic size={34} strokeWidth={1.8} />
            </span>
            <h2 className="ac-title-2">{t("academy.speaking.micTitle")}</h2>
            <p className="ac-secondary ac-stage__text">{t("ielts.speaking.micHint")}</p>
            {noRecorder ? (
              <p className="ko">{t("academy.speaking.noRecorder")}</p>
            ) : (
              <Button size="lg" icon={<Mic aria-hidden size={18} />} onClick={() => void testMicrophone()}>
                {t("academy.speaking.micTest")}
              </Button>
            )}
            {micError && (
              <p className="ko ac-status" role="alert">
                <MicOff aria-hidden size={18} />
                {t("academy.speaking.micDenied")}
              </p>
            )}
          </div>
        )}

        {phase === "ready" && (
          <div className="ac-stage">
            <h2 className="ac-title-2">{t("ielts.speaking.readyTitle")}</h2>
            <LevelMeter level={level} label={t("academy.speaking.level")} />
            <ul className="ac-eo-tasks">
              {state.tasks.map((item) => (
                <li key={item.id}>
                  <span className="ac-task-tab__n ao-num" aria-hidden="true">
                    {item.number}
                  </span>
                  <span>{t(`ielts.speaking.part${item.number}`, { defaultValue: t("ielts.speaking.partN", { n: item.number }) })}</span>
                  <span className="ac-caption ao-num">{clock(item.seconds)}</span>
                </li>
              ))}
            </ul>
            <p className="ac-footnote ac-stage__text">{t("ielts.speaking.rules")}</p>
            {task ? (
              <Button size="lg" onClick={() => void beginPart()}>
                {t("academy.speaking.start")}
              </Button>
            ) : (
              <Button size="lg" onClick={() => setPhase("review")}>
                {t("academy.player.continue")}
              </Button>
            )}
          </div>
        )}

        {task && (phase === "opening" || phase === "prep" || phase === "record") && (
          <div className="ac-stage ac-stage--task">
            <p className="ac-player__eyebrow">{t("ielts.speaking.partOf", { n: task.number, total: state.tasks.length })}</p>
            {card && (
              <article className="ac-cue-card" aria-labelledby="ielts-cue">
                <p className="ac-cue-card__eyebrow">
                  <NotebookPen aria-hidden size={14} /> {t("ielts.speaking.cueCard")}
                </p>
                <h2 id="ielts-cue" className="ac-cue-card__topic" lang="en">
                  {card.topic}
                </h2>
                {card.bullets.length > 0 && (
                  <>
                    <p className="ac-cue-card__lead" lang="en">
                      {t("ielts.examinerEn.youShouldSay")}
                    </p>
                    <ul lang="en">
                      {card.bullets.map((bullet) => (
                        <li key={bullet}>{bullet}</li>
                      ))}
                    </ul>
                  </>
                )}
              </article>
            )}
            {phase === "opening" && (
              <div role="status" className="ac-stage__status">
                <Examiner speaking label={t("ielts.speaking.examinerSpeaks")} />
                {opening && (
                  <p className="ac-bubble ac-bubble--them" lang="en">
                    {opening}
                  </p>
                )}
              </div>
            )}
            {(phase === "prep" || phase === "record") && (
              <div className={`ac-countdown ac-countdown--${phase}${shownLeft <= 10 ? " ac-countdown--end" : ""}`}>
                <Ring ratio={shownTotal ? shownLeft / shownTotal : 0} size={180} stroke={12} tone={phase === "record" ? "eo" : "accent"}>
                  <span className="ac-countdown__state">
                    {phase === "record" ? (
                      <>
                        <span className="ac-rec-dot" aria-hidden="true" />
                        <span role="status">{live.longTurn ? t("ielts.speaking.longTurn") : t("academy.speaking.recording")}</span>
                      </>
                    ) : (
                      t("ielts.speaking.prep")
                    )}
                  </span>
                  <span className="ac-countdown__digits ao-num" role="timer" aria-label={phase === "prep" ? t("ielts.speaking.prep") : t("academy.speaking.timeLeft")}>
                    {clock(shownLeft)}
                  </span>
                </Ring>
              </div>
            )}
            {phase === "prep" && <p className="ac-secondary ac-stage__text">{t("ielts.speaking.prepHint")}</p>}
            {micSilent && <p className="ac-note ac-note--warning" role="alert">{t("academy.redo.micSilent")}</p>}
            {phase === "record" && (
              <>
                <LevelMeter level={level} label={t("academy.speaking.level")} />
                <div className="ac-live-examiner" aria-live="polite">
                  <p className="ac-status ac-secondary" role="status">
                    {live.status !== "listening" && <span className="ac-dot ac-dot--busy" aria-hidden="true" />}
                    {t(`ielts.speaking.status.${live.status}`)}
                  </p>
                  {live.offline && <p className="ac-footnote">{t("academyExaminer.offline")}</p>}
                  {live.lines.length > 0 && (
                    <ul className="ac-transcript" aria-label={t("academyExaminer.conversation")}>
                      {live.lines.map((line, index) => (
                        <li key={`${index}-${line.role}`} className={`ac-bubble ac-bubble--${line.role === "examiner" ? "them" : "me"}`}>
                          <strong className="ac-bubble__who">{t(`ielts.speaking.role.${line.role}`)}</strong> <span lang="en">{line.text}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <Button icon={<MessageSquareReply aria-hidden size={16} />} onClick={live.finishTurn} disabled={live.status !== "listening"}>
                    {live.longTurn ? t("ielts.speaking.finishedLongTurn") : t("ielts.speaking.finishedAnswer")}
                  </Button>
                </div>
                <Button variant="ghost" icon={<Square aria-hidden size={14} />} onClick={stopRecording}>
                  {t("ielts.speaking.endPart")}
                </Button>
              </>
            )}
          </div>
        )}

        {phase === "upload" && (
          <div className="ac-stage">
            <p className="ac-status" role="status">
              <span className="ac-dot ac-dot--busy" aria-hidden="true" />
              {t("academy.speaking.uploading")}
            </p>
          </div>
        )}
        {phase === "uploadError" && task && (
          <div className="ac-stage" role="alert">
            <p className="ko">{t("academy.speaking.uploadError")}</p>
            <Button onClick={() => pendingBlob.current && void upload(pendingBlob.current, task)}>{t("common.retry")}</Button>
          </div>
        )}
        {phase === "between" && (
          <div className="ac-stage">
            <p className="ac-status ac-status--success" role="status">
              <span lang="en">{t("ielts.examinerEn.thanks")}</span>
            </p>
          </div>
        )}
        {phase === "review" && (
          <div className="ac-stage ac-stage--review">
            <h2 className="ac-title-2">{t("ielts.speaking.doneTitle")}</h2>
            <p className="ac-secondary ac-stage__text">{t("ielts.speaking.doneText")}</p>
            <Button size="lg" onClick={() => void finish()} disabled={finishing}>
              {finishing ? t("common.loading") : t("academy.speaking.finish")}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
